import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ScrollText, ShieldCheck, Loader2, LogOut } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

/**
 * Early Access Community & Solicitation Agreement modal.
 *
 * Mounted from SiteShell so it renders on every authenticated route.
 *
 * Visibility rules:
 *   * user is signed in and NOT ephemeral
 *   * ``user.is_private_test === true`` (Early Access active)
 *   * user is NOT an admin (admins bypass)
 *   * ``user.early_access_agreement_accepted_version`` is missing OR is
 *     less than the server's current agreement version (bumping the
 *     server constant re-prompts every existing member).
 *
 * The modal is non-dismissible — the only exits are:
 *   1) Check the box and tap "Agree & Continue" (records acceptance
 *      via POST /api/private-tester-requests/me/accept-agreement)
 *   2) Log out (via POST /api/auth/logout then hard reload to /)
 *
 * There is no close (X) button, no Escape-to-close, and no backdrop click
 * dismissal. This is by design — access to private testing features MUST
 * be gated on acceptance of the current version of the agreement.
 *
 * ADMIN PREVIEW MODE
 * ------------------
 * Passing ``previewMode`` renders the modal for an admin reviewer with
 * NO user-state gate, NO API side effects (no accept, no refresh), and
 * swaps the footer for a single ``Close preview`` button that invokes
 * ``onClose``. Content, layout, and styling are otherwise identical to
 * what real users see, so the admin can review the exact experience.
 */
export default function EarlyAccessCommunityAgreementModal({ previewMode = false, onClose } = {}) {
  const { user, refresh, logout } = useAuth();
  const [agreement, setAgreement] = useState(null);
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const isAdmin = (user?.roles || []).includes("admin");
  const isPT = !!user?.is_private_test;

  const acceptedVersion = Number(user?.early_access_agreement_accepted_version || 0);
  const needsAccept = !!agreement && acceptedVersion < Number(agreement.version);

  useEffect(() => {
    if (!previewMode && (!user || isAdmin || !isPT || user.is_ephemeral)) return;
    // Only fetch the agreement once per session for eligible users (or
    // immediately in admin preview mode).
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/private-tester-requests/agreement");
        if (!cancelled) setAgreement(data);
      } catch {
        // Silent — if the fetch fails we simply don't show the modal
        // this session; user will be re-prompted next visit.
      }
    })();
    return () => { cancelled = true; };
  }, [user, isAdmin, isPT, previewMode]);

  if (!previewMode) {
    if (!user || isAdmin || !isPT || user.is_ephemeral) return null;
    if (!needsAccept) return null;
  }
  if (!agreement) return null;

  const accept = async () => {
    if (!checked || saving) return;
    setSaving(true);
    try {
      await api.post("/private-tester-requests/me/accept-agreement", {});
      if (typeof refresh === "function") {
        try { await refresh(); } catch { /* silent */ }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doLogout = async () => {
    setLoggingOut(true);
    try {
      if (typeof logout === "function") {
        await logout();
      } else {
        try { await api.post("/auth/logout", {}); } catch { /* silent */ }
      }
    } finally {
      // Hard reload to strip any in-memory state and reroute to landing.
      window.location.href = "/";
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60 sm:p-4 overflow-y-auto"
      data-testid="early-access-agreement-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="early-access-agreement-title"
    >
      <div
        className="w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl bg-card shadow-2xl border max-h-[92vh] sm:max-h-[calc(100vh-4rem)] flex flex-col"
        data-testid="early-access-agreement-modal"
      >
        <header className="shrink-0 flex items-center gap-3 px-5 sm:px-6 pt-5 pb-3 border-b bg-card rounded-t-2xl">
          <div className="w-9 h-9 rounded-full bg-[hsl(var(--sb-forest))] text-[hsl(42_60%_96%)] grid place-items-center shrink-0">
            <ScrollText size={18} />
          </div>
          <h3
            id="early-access-agreement-title"
            className="font-serif text-xl sm:text-2xl leading-tight"
          >
            {agreement.title}
          </h3>
        </header>

        <div
          className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 pt-4 pb-4 space-y-4 text-sm md:text-[15px] leading-relaxed text-foreground/85"
          data-testid="early-access-agreement-body"
        >
          <p>{agreement.intro}</p>
          <p className="font-semibold text-foreground">{agreement.lead_in}</p>
          <ul className="space-y-2.5 list-none pl-0">
            {agreement.bullets.map((b, i) => {
              const dot = b.indexOf(".");
              const heading = dot > 0 ? b.slice(0, dot + 1) : "";
              const rest = dot > 0 ? b.slice(dot + 1).trimStart() : b;
              return (
                <li
                  key={i}
                  className="flex gap-2.5"
                  data-testid={`early-access-agreement-bullet-${i}`}
                >
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[hsl(var(--sb-forest))] shrink-0" aria-hidden="true" />
                  <span>
                    {heading && (
                      <strong className="font-semibold text-foreground">{heading}</strong>
                    )}
                    {heading ? " " : ""}
                    {rest}
                  </span>
                </li>
              );
            })}
          </ul>
          {agreement.outro ? (
            <div className="rounded-xl border border-[hsl(var(--sb-clay))]/30 bg-[hsl(var(--sb-clay))]/10 px-3 py-2.5 text-[13.5px] leading-snug flex items-start gap-2">
              <ShieldCheck size={14} className="mt-0.5 shrink-0 text-[hsl(var(--sb-clay))]" />
              <span>{agreement.outro}</span>
            </div>
          ) : null}
          <label className="flex items-start gap-2.5 rounded-xl border border-border px-3 py-2.5 cursor-pointer hover:bg-muted/40 transition-colors">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0"
              data-testid="early-access-agreement-checkbox"
            />
            <span className="text-sm font-medium text-foreground">
              {agreement.checkbox_label}
            </span>
          </label>
          <p className="text-xs text-muted-foreground">
            Version {agreement.version} — you can review this anytime under{" "}
            <Link
              to="/early-access-agreement"
              className="underline underline-offset-4"
              onClick={(e) => e.stopPropagation()}
              target="_blank"
              rel="noreferrer"
              data-testid="early-access-agreement-review-link"
            >
              Policies · Early Access Community Agreement
            </Link>
            .
          </p>
        </div>

        <footer
          className="shrink-0 border-t bg-card rounded-b-2xl px-5 sm:px-6 py-4 flex flex-col-reverse sm:flex-row sm:items-center gap-2 sm:gap-3"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)" }}
        >
          {previewMode ? (
            <>
              <div className="hidden sm:block sm:flex-1" />
              <button
                type="button"
                onClick={() => onClose?.()}
                className="btn-primary w-full sm:w-auto"
                data-testid="early-access-agreement-preview-close"
              >
                Close preview
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={doLogout}
                disabled={loggingOut || saving}
                className="btn-ghost border w-full sm:w-auto inline-flex items-center justify-center gap-1.5 text-sm font-medium"
                data-testid="early-access-agreement-logout"
              >
                <LogOut size={14} /> {loggingOut ? "Logging out…" : "Log out"}
              </button>
              <div className="hidden sm:block sm:flex-1" />
              <button
                type="button"
                onClick={accept}
                disabled={!checked || saving}
                className="btn-primary w-full sm:w-auto"
                data-testid="early-access-agreement-accept"
              >
                {saving ? <><Loader2 className="animate-spin" size={14} /> Saving…</> : agreement.button_label}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
